import produce from 'immer';
import _, {zip} from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {ClientId} from 'notebook/ELNConstants';
import {
  BaseDedicatedLookupStructuredTableColumn,
  GenericStructuredTableColumn,
  StructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import excess from 'notebook/tables/excess';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {AnyStructuredTable, NotePartWithItem} from 'notebook/util/NoteUtilConstants';
import APIUtil from 'util/APIUtil';

function _isDedicatedLookupColumn(
  column: GenericStructuredTableColumn<ClientId>
): column is BaseDedicatedLookupStructuredTableColumn<ClientId> {
  return column.columnType === TableColumnType.DEDICATED_LOOKUP;
}

/**
 * Convert a structured table to a shape that can be pasted from & to templates. (We enforce whether
 * the copy / paste is across templates in StructuredTablePlugin).
 *
 * This is a helper function for `Editor.convertItemForPaste`.
 *
 * Note: If structured table properties are modified, PasteStructuredTableSentinel-test.ts will throw a type
 * error and indicate to the engineer to handle the modification's impact on copy / paste in this function.
 */
export default function convertStructuredTableForPaste(
  item: NotePartWithItem<AnyStructuredTable>,
  usedTableNames: Array<string>,
  destinationFileId?: string,
  isDestinationConfig?: boolean
): {item: NotePartWithItem<AnyStructuredTable>; name: string} {
  const newItem = produce(item, (draftItem) => {
    let name = item.item.name;

    // Table
    // - Generate new table name
    // - Generate new provenance UUID
    // - Generate new cid for table
    if (Array.from(usedTableNames).includes(name)) {
      name = excess.util.uniqueTableName(
        usedTableNames,
        excess.util.extractTableNamePrefix(draftItem.item.name)
      );
      draftItem.item.name = name;
    }
    draftItem.item.provenanceUuid = uuidv4();
    draftItem.item.cid = _.uniqueId('st');
    draftItem.item.apiId = undefined;

    // Convert config tables if destination is an entry or stage entry
    // This can happen on sub-template insert
    if (destinationFileId) {
      draftItem.item.fileId = destinationFileId;
      draftItem.item.isConfig = Boolean(
        APIUtil.isEntryOrSubOrWorkflowTemplate(destinationFileId) ||
          (APIUtil.isDocument(destinationFileId) && isDestinationConfig)
      );
    }

    // Rows
    // - Generate new provenance UUID
    const newRows = [];
    // TODO: BNCH-80679: Handle parameter table copies
    for (const row of draftItem.item.rows) {
      newRows.push({...row, rowId: uuidv4()});
    }
    draftItem.item.rows = newRows;

    // Columns
    // - Generate new provenance UUID
    // - Generate new cids
    // - Re-map inputSourceProvenanceUuid for lookup columns
    const newColumns = [];
    for (const column of draftItem.item.columns) {
      const newColumn = {...column, cid: _.uniqueId('dtcol'), provenanceUuid: uuidv4()};
      newColumns.push(newColumn);
    }
    // Re-map inputSourceProvenanceUuid after new columns have been initialized with a new prov. UUID
    for (const [colIdx, column] of draftItem.item.columns.entries()) {
      if (_isDedicatedLookupColumn(column)) {
        const {inputColumnProvenanceUuid} = column;
        const inputColumnIndex = draftItem.item.columns.findIndex(
          (column) => column.provenanceUuid === inputColumnProvenanceUuid
        );
        assert(
          inputColumnIndex !== -1,
          'Found a dedicated lookup column whose inputColumnProvenanceUuid refers to a column that is not present in the table.'
        );

        const newLookupColumn = newColumns[colIdx] as BaseDedicatedLookupStructuredTableColumn<ClientId>;
        assert(
          _isDedicatedLookupColumn(newLookupColumn),
          'Columns during table copy & paste were pasted in incorrect order. Found a non-lookup column in pasted table in the same position as a lookup column in the original table.'
        );
        newLookupColumn.inputColumnProvenanceUuid = newColumns[inputColumnIndex].provenanceUuid;
      }
    }
    draftItem.item.columns = newColumns;
  });

  return {item: newItem, name: newItem.item.name};
}

export interface ChangedStructuredTableUuids {
  oldToNewStructuredTableUuids: {[oldUuid: string]: string | undefined};
  oldToNewStructuredTableColumnUuids: {
    [oldTableUuid: string]: {[oldColumnUuid: string]: string | undefined} | undefined;
  };
}

/**
 * Given a list of old Structured Tables and new Structured Tables, create mappings from the old uuids to new
 * uuids. This expects that the tables and their columns are the same shape and order in both lists.
 *
 * This is useful when we still have references to the old uuids following a paste or insert.
 */
export function getChangedStructuredTableUuids(
  oldStructuredTables: Array<UnknownStructuredTable>,
  newStructuredTables: Array<UnknownStructuredTable>
): ChangedStructuredTableUuids {
  const oldToNewStructuredTableUuids: ChangedStructuredTableUuids['oldToNewStructuredTableUuids'] = {};
  const oldToNewStructuredTableColumnUuids: ChangedStructuredTableUuids['oldToNewStructuredTableColumnUuids'] =
    {};
  assert(
    oldStructuredTables.length === newStructuredTables.length,
    'Lists of structured tables should be the same length when trying to check changed UUIDs'
  );
  for (const [oldTable, newTable] of zip(oldStructuredTables, newStructuredTables) as Array<
    [UnknownStructuredTable, UnknownStructuredTable]
  >) {
    const oldTableUuid = oldTable.provenanceUuid;
    const newTableUuid = newTable.provenanceUuid;
    oldToNewStructuredTableUuids[oldTableUuid] = newTableUuid;
    oldToNewStructuredTableColumnUuids[oldTableUuid] = {};
    assert(
      oldTable.columns.length === newTable.columns.length,
      'Structured Tables should have the same number of columns when trying to check changed UUIDs'
    );
    for (const [oldColumn, newColumn] of zip(oldTable.columns, newTable.columns) as Array<
      [StructuredTableColumn, StructuredTableColumn]
    >) {
      const oldColumnUuid = oldColumn.provenanceUuid;
      const newColumnUuid = newColumn.provenanceUuid;
      oldToNewStructuredTableColumnUuids[oldTableUuid][oldColumnUuid] = newColumnUuid;
    }
  }
  return {oldToNewStructuredTableUuids, oldToNewStructuredTableColumnUuids};
}
